/**
 * @brisa/shared/observability/legal — wrappers canônicos para eventos da
 * feature legal-documents-v1 (METRICS_SPEC §3 Eventos 2-4).
 *
 * Inclui as 4 propriedades canônicas obrigatórias em todo evento da feature
 * (`user_id`, `session_id`, `feature_flag_active`, `release`) — auto-injetadas
 * pelo wrapper para os callers não esquecerem.
 */
import { gaEvent } from './ga';
import { clarityEvent } from './clarity';
import { logger } from '../logger';

/**
 * Slug canônico de documento legal. Replicado aqui (em vez de importado de
 * `@brisa/identity`) porque `@brisa/shared` está em camada mais baixa da
 * hierarquia de packages e não pode depender de módulos de domínio
 * (princípio modular — `tsconfig.json` de shared tem rootDir restrito a src/).
 * O type fonte de verdade está em `packages/modules/identity/src/services/consent-versions.ts`.
 */
export type LegalDocumentSlug =
  | 'tos'
  | 'privacy'
  | 'cookies'
  | 'refund'
  | 'pause'
  | 'age_18_plus';

/**
 * 4 propriedades canônicas obrigatórias em qualquer evento da feature
 * (METRICS_SPEC §0.5). `release` é auto-injetado a partir do hash do commit
 * Vercel; callers só fornecem os 3 primeiros.
 */
interface BaseLegalEventProps {
  user_id: string;
  session_id: string;
  feature_flag_active: boolean;
}

function withCanonical<T extends object>(
  props: T,
  base: BaseLegalEventProps,
): Record<string, unknown> {
  return {
    ...base,
    ...props,
    release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? 'local',
    timestamp: Date.now(),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Evento 2 — Legal Document Viewed (P1 — client-side, após hidratação)
// ─────────────────────────────────────────────────────────────────────────

export interface LegalDocumentViewedProps {
  document_slug: LegalDocumentSlug;
  document_version: string;
  is_archived: boolean;
  locale: 'pt' | 'es';
  is_authenticated: boolean;
  referrer_path: string | null;
}

export function trackLegalDocumentViewed(
  props: LegalDocumentViewedProps,
  base: BaseLegalEventProps,
): void {
  const fullProps = withCanonical(props, base);
  gaEvent('legal_document_viewed', fullProps);
  clarityEvent('legal_document_viewed', fullProps);
  logger.info('Legal Document Viewed', fullProps);
}

// ─────────────────────────────────────────────────────────────────────────
// Evento 3 — Legal Locale Toggled (P2 — server-side log + GA4 quando o
// callback client-side estiver disponível; aqui cobrimos só o lado server,
// que é o caminho em que setLocaleAction roda)
// ─────────────────────────────────────────────────────────────────────────

export interface LegalLocaleToggledProps {
  from_locale: 'pt' | 'es';
  to_locale: 'pt' | 'es';
  pathname: string;
  is_authenticated: boolean;
}

export function logLegalLocaleToggled(
  props: LegalLocaleToggledProps,
  base: BaseLegalEventProps,
): void {
  // Server-side Server Action: logger.info é o sink canônico.
  // GA4/Clarity ficam por conta do redirect resultante (próximo page-view
  // dispara `legal_document_viewed` com o novo locale, fechando a triangulação).
  logger.info('Legal Locale Toggled', withCanonical(props, base));
}

// ─────────────────────────────────────────────────────────────────────────
// Evento 4 — Legal Drawer Opened (P2 — client-side, ToC mobile)
// ─────────────────────────────────────────────────────────────────────────

export interface LegalDrawerOpenedProps {
  document_slug: LegalDocumentSlug;
  document_version: string;
  locale: 'pt' | 'es';
}

export function trackLegalDrawerOpened(
  props: LegalDrawerOpenedProps,
  base: BaseLegalEventProps,
): void {
  const fullProps = withCanonical(props, base);
  gaEvent('legal_drawer_opened', fullProps);
  clarityEvent('legal_drawer_opened', fullProps);
}
